import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown, ArrowUpRight, Flame, TrendingDown, CloudRain } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Skeleton from '../components/common/Skeleton';
import GradingLoader from '../components/common/GradingLoader';
import LeagueHeader from '../components/common/LeagueHeader';
import { League, V1Standings, V1DraftAnalysis } from '../types';
import { v1Api, analyticsApi } from '../services/api';
import DraftBoard from '../components/draft/DraftBoard';
import SeasonSuperlatives from '../components/analytics/SeasonSuperlatives';

interface SuperlativeWinner {
  team_id: number;
  team_name: string;
  player_name?: string;
  value: number;
  display: string;
}
interface Superlative {
  award: string;
  description: string;
  winner: SuperlativeWinner;
}

const LeaguePage: React.FC = () => {
  const { espnLeagueId } = useParams<{ espnLeagueId: string }>();
  const navigate = useNavigate();
  const [league, setLeague] = useState<League | null>(null);
  const [standings, setStandings] = useState<V1Standings | null>(null);
  const [draftAnalysis, setDraftAnalysis] = useState<V1DraftAnalysis | null>(null);
  const [superlatives, setSuperlatives] = useState<Superlative[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'draft' | 'awards'>('overview');
  const [expandedTeam, setExpandedTeam] = useState<number | null>(null);
  const [sortField, setSortField] = useState<'rank' | 'team_name' | 'regular_season_rank' | 'regular_season_record' | 'points_for' | 'points_against' | 'differential'>('rank');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchLeagueData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load league data first to get season information
      const leagueData = await v1Api.leagues.getByEspnId(espnLeagueId!);

      // Load remaining data with season information
      const standingsData = await v1Api.analytics.getStandings(parseInt(espnLeagueId!), leagueData.season); // Pass both league ID and season

      setLeague(leagueData);
      setStandings(standingsData);
      setLoading(false);

      // Load draft analysis separately (it's only needed for draft tab)
      v1Api.analytics.getDraftAnalysis(leagueData.id) // Use internal league ID for draft analysis
        .then(draftData => {
          setDraftAnalysis(draftData);
        })
        .catch(err => {
          console.warn('Draft analysis load failed:', err);
        });

      // Load superlatives for the "Season at a glance" insight panel (data already computed by awards API)
      analyticsApi.getSuperlatives(parseInt(espnLeagueId!))
        .then(data => {
          setSuperlatives(data.superlatives || null);
        })
        .catch(err => {
          console.warn('Superlatives load failed:', err);
        });

    } catch (err) {
      setError('Failed to load league data');
      console.error('League fetch error:', err);
      setLoading(false);
    }
  }, [espnLeagueId]);

  useEffect(() => {
    if (espnLeagueId) {
      fetchLeagueData();
    }
  }, [espnLeagueId, fetchLeagueData]);

  const handleTeamClick = (teamId: number) => {
    navigate(`/leagues/espn/${espnLeagueId}/teams/${teamId}`);
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'rank' || field === 'regular_season_rank' ? 'asc' : 'desc');
    }
  };

  const getSortedStandings = (standingsData: any[]) => {
    return [...standingsData].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (sortField) {
        case 'rank':
          valueA = a.rank;
          valueB = b.rank;
          break;
        case 'team_name':
          valueA = a.team_name.toLowerCase();
          valueB = b.team_name.toLowerCase();
          break;
        case 'regular_season_rank':
          valueA = a.regular_season?.rank || 0;
          valueB = b.regular_season?.rank || 0;
          break;
        case 'regular_season_record':
          valueA = a.regular_season?.wins || 0;
          valueB = b.regular_season?.wins || 0;
          break;
        case 'points_for':
          valueA = a.regular_season?.points_for || 0;
          valueB = b.regular_season?.points_for || 0;
          break;
        case 'points_against':
          valueA = a.regular_season?.points_against || 0;
          valueB = b.regular_season?.points_against || 0;
          break;
        case 'differential':
          valueA = a.regular_season?.point_differential || 0;
          valueB = b.regular_season?.point_differential || 0;
          break;
        default:
          valueA = a.rank;
          valueB = b.rank;
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader: React.FC<{ field: typeof sortField; children: React.ReactNode; className?: string }> = ({ field, children, className = "" }) => (
    <th
      className={`cursor-pointer hover:bg-background-tertiary/50 transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : className.includes('text-center') ? 'justify-center' : 'justify-start'}`}>
        {children}
        {sortField === field && (
          sortDirection === 'asc' ?
            <ChevronUp className="h-3 w-3" /> :
            <ChevronDown className="h-3 w-3" />
        )}
      </div>
    </th>
  );

  // Metadata line: "2025 · 12 teams · 14-week regular season · 16 rounds"
  const buildMetaLine = (): string => {
    if (!league) return '';
    const parts: string[] = [`${league.season}`, `${league.team_count} teams`];
    const regWeeks = league.regular_season_weeks ||
      (standings?.summary ? Math.floor(standings.summary.regular_season_games / (league.team_count / 2)) : null);
    if (regWeeks) parts.push(`${regWeeks}-week regular season`);
    const rounds = league.summary?.total_picks
      ? Math.floor(league.summary.total_picks / league.team_count)
      : (draftAnalysis?.summary.total_picks ? Math.floor(draftAnalysis.summary.total_picks / league.team_count) : null);
    if (rounds) parts.push(`${rounds} rounds`);
    return parts.join('  ·  ');
  };

  // Champion strip data
  const champion = standings?.summary?.champion;
  const secondPlace = standings?.standings?.find(t => t.rank === 2);
  const thirdPlace = standings?.standings?.find(t => t.rank === 3);
  const rsLeader = standings?.summary?.regular_season_leader;

  const recordStr = (wins?: number, losses?: number, pct?: number) =>
    `${wins ?? 0}-${losses ?? 0} · ${((pct ?? 0) * 100).toFixed(1)}%`;

  // "Season at a glance" insights sourced from awards/superlatives API
  const findAward = (name: string) => superlatives?.find(s => s.award === name);
  const insights = [
    { icon: Flame, label: 'Draft Boom', award: findAward('Draft Boom of the Year') },
    { icon: TrendingDown, label: 'Biggest Bust', award: findAward('Biggest Draft Bust') },
    { icon: CloudRain, label: 'Unluckiest Loss', award: findAward('Unluckiest Loss') },
  ].filter(i => i.award);

  if (!espnLeagueId) {
    return (
      <div className="min-h-[100dvh] bg-background-primary flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Invalid league ID</h2>
          <Link to="/">
            <Button className="mt-4">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-background-primary">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-10 w-80 mb-3" />
          <Skeleton className="h-4 w-96 mb-8" />
          <Skeleton className="h-28 w-full mb-8" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="min-h-[100dvh] bg-background-primary flex items-center justify-center">
        <Card className="p-6 text-center">
          <div className="text-accent-danger mb-4">{error || 'Failed to load league'}</div>
          <Button onClick={fetchLeagueData} className="mr-4">Retry</Button>
          <Link to="/">
            <Button variant="secondary">Back to Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  // ---- Champion strip cell ----
  const StripCell: React.FC<{
    label: string;
    name?: string;
    sub?: string;
    tickColor: string;
    emphasis?: boolean;
  }> = ({ label, name, sub, tickColor, emphasis }) => (
    <div className={`flex items-stretch gap-3 ${emphasis ? 'py-1' : ''}`}>
      <div className="w-[3px] rounded-full flex-shrink-0" style={{ background: tickColor }} />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-text-secondary">{label}</p>
        <p className={`font-semibold text-text-primary truncate ${emphasis ? 'text-xl' : 'text-base'}`}>
          {name || '—'}
        </p>
        {sub && <p className="font-mono text-sm text-text-secondary mt-0.5">{sub}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background-primary">
      <LeagueHeader showBackButton={true} />
      <style>{`
        .overview-layout {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        @media (min-width: 1024px) {
          .overview-layout {
            display: grid;
            grid-template-columns: 1fr 2fr;
            gap: 2rem;
            align-items: start;
          }
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* League title + metadata line (League Summary card demoted) */}
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-text-primary tracking-tight mb-1.5">
            {league.name || 'League'}
          </h1>
          <p className="font-mono text-sm text-text-secondary">{buildMetaLine()}</p>
        </div>

        {/* Champion strip — one panel, hairline dividers, champion larger */}
        {champion && (
          <Card className="p-5 mb-8">
            {/* Desktop / tablet: horizontal strip divided by hairlines */}
            <div className="hidden sm:grid sm:grid-cols-[1.5fr_1fr_1fr_1.5fr] sm:divide-x sm:divide-fieldline">
              <div className="pr-5">
                <StripCell
                  label="Champion"
                  name={champion.team_name}
                  sub={recordStr(champion.total_season.wins, champion.total_season.losses, champion.total_season.win_percentage)}
                  tickColor="#2FBF71"
                  emphasis
                />
              </div>
              <div className="px-5">
                <StripCell
                  label="2nd Place"
                  name={secondPlace?.team_name}
                  sub={secondPlace ? recordStr(secondPlace.total_season.wins, secondPlace.total_season.losses, secondPlace.total_season.win_percentage) : undefined}
                  tickColor="rgba(139,146,168,0.6)"
                />
              </div>
              <div className="px-5">
                <StripCell
                  label="3rd Place"
                  name={thirdPlace?.team_name}
                  sub={thirdPlace ? recordStr(thirdPlace.total_season.wins, thirdPlace.total_season.losses, thirdPlace.total_season.win_percentage) : undefined}
                  tickColor="rgba(139,146,168,0.35)"
                />
              </div>
              <div className="pl-5">
                <StripCell
                  label="Reg. Season Leader"
                  name={rsLeader?.team_name}
                  sub={rsLeader ? recordStr(rsLeader.regular_season?.wins, rsLeader.regular_season?.losses, rsLeader.regular_season?.win_percentage) : undefined}
                  tickColor="rgba(139,146,168,0.6)"
                />
              </div>
            </div>
            {/* Mobile: compact 2x2 grid in one panel */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-4 sm:hidden">
              <StripCell label="Champion" name={champion.team_name}
                sub={recordStr(champion.total_season.wins, champion.total_season.losses, champion.total_season.win_percentage)}
                tickColor="#2FBF71" />
              <StripCell label="2nd Place" name={secondPlace?.team_name}
                sub={secondPlace ? recordStr(secondPlace.total_season.wins, secondPlace.total_season.losses, secondPlace.total_season.win_percentage) : undefined}
                tickColor="rgba(139,146,168,0.6)" />
              <StripCell label="3rd Place" name={thirdPlace?.team_name}
                sub={thirdPlace ? recordStr(thirdPlace.total_season.wins, thirdPlace.total_season.losses, thirdPlace.total_season.win_percentage) : undefined}
                tickColor="rgba(139,146,168,0.35)" />
              <StripCell label="Reg. Leader" name={rsLeader?.team_name}
                sub={rsLeader ? recordStr(rsLeader.regular_season?.wins, rsLeader.regular_season?.losses, rsLeader.regular_season?.win_percentage) : undefined}
                tickColor="rgba(139,146,168,0.6)" />
            </div>
          </Card>
        )}

        {/* Tab Navigation — underline segmented control, sticky on scroll */}
        <div className="sticky top-16 z-30 -mx-4 px-4 sm:mx-0 sm:px-0 bg-background-primary/95 backdrop-blur-sm mb-8">
          <div className="flex gap-6 border-b border-fieldline">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'draft', label: 'Draft' },
              { key: 'awards', label: 'Awards' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={`relative py-3 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${
                  activeTab === key
                    ? 'text-text-primary'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {label}
                {activeTab === key && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-accent-primary rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}

        {activeTab === 'draft' && (
          draftAnalysis ? (
            <DraftBoard
              leagueId={league?.id || 0}
              onTeamSelect={handleTeamClick}
              onPlayerSelect={(playerId) => navigate(`/leagues/espn/${espnLeagueId}/players/${playerId}`)}
            />
          ) : (
            <Card className="p-6">
              <GradingLoader label="Scoring every pick…" size="lg" className="py-12" />
            </Card>
          )
        )}

        {activeTab === 'awards' && (
          <SeasonSuperlatives leagueId={parseInt(espnLeagueId!) || 0} />
        )}

        {activeTab === 'overview' && (
          <div className="overview-layout">
            {/* Left Column — Season at a glance insight panel */}
            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="text-base font-semibold text-text-primary mb-4">Season at a glance</h3>
                {insights.length > 0 ? (
                  <div className="divide-y divide-fieldline">
                    {insights.map(({ icon: Icon, label, award }) => (
                      <button
                        key={label}
                        onClick={() => setActiveTab('awards')}
                        className="w-full flex items-start gap-3 py-3 text-left group first:pt-0 last:pb-0"
                      >
                        <Icon className="w-4 h-4 mt-0.5 text-text-secondary flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs uppercase tracking-wide text-text-secondary">{label}</p>
                          <p className="text-text-primary font-medium truncate">{award!.winner.team_name}</p>
                          <p className="font-mono text-sm text-text-secondary truncate">{award!.winner.display}</p>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-secondary text-sm">Loading insights…</p>
                )}
              </Card>
            </div>

            {/* Right Column — League Standings */}
            <div>
              {standings && (
                <Card className="p-6">
                  <h2 className="text-xl font-semibold text-text-primary mb-6">League Standings</h2>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-fieldline">
                          <SortableHeader field="rank" className="text-left py-3 px-4 text-text-secondary font-medium text-sm">
                            <div className="flex flex-col items-start">
                              <span>Final Rank</span>
                              <span className="text-xs text-text-tertiary font-normal">Playoff Result</span>
                            </div>
                          </SortableHeader>
                          <SortableHeader field="team_name" className="text-left py-3 px-4 text-text-secondary font-medium text-sm">
                            Team
                          </SortableHeader>
                          <SortableHeader field="regular_season_rank" className="text-center py-3 px-4 text-text-secondary font-medium text-sm">
                            <div className="flex flex-col items-center">
                              <span>Reg. Rank</span>
                              <span className="text-xs text-text-tertiary font-normal">Weeks 1-14</span>
                            </div>
                          </SortableHeader>
                          <SortableHeader field="regular_season_record" className="text-center py-3 px-4 text-text-secondary font-medium text-sm">
                            <div className="flex flex-col items-center">
                              <span>Reg. Season</span>
                              <span className="text-xs text-text-tertiary font-normal">W-L Record</span>
                            </div>
                          </SortableHeader>
                          <SortableHeader field="points_for" className="text-right py-3 px-4 text-text-secondary font-medium text-sm">
                            <div className="flex flex-col items-end">
                              <span>Points For</span>
                              <span className="text-xs text-text-tertiary font-normal">Regular Season</span>
                            </div>
                          </SortableHeader>
                          <SortableHeader field="points_against" className="text-right py-3 px-4 text-text-secondary font-medium text-sm">
                            <div className="flex flex-col items-end">
                              <span>Points Against</span>
                              <span className="text-xs text-text-tertiary font-normal">Regular Season</span>
                            </div>
                          </SortableHeader>
                          <SortableHeader field="differential" className="text-right py-3 px-4 text-text-secondary font-medium text-sm">
                            <div className="flex flex-col items-end">
                              <span>Differential</span>
                              <span className="text-xs text-text-tertiary font-normal">Regular Season</span>
                            </div>
                          </SortableHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedStandings(standings.standings).map((team) => {
                          const madePlayoffs = team.rank <= 6;
                          return (
                            <tr
                              key={team.team_id}
                              className="border-b border-fieldline hover:bg-background-tertiary/60 cursor-pointer transition-colors"
                              style={{
                                boxShadow: team.is_champion
                                  ? 'inset 3px 0 0 0 #2FBF71'
                                  : madePlayoffs
                                    ? 'inset 3px 0 0 0 rgba(47,191,113,0.35)'
                                    : 'inset 3px 0 0 0 transparent',
                              }}
                              onClick={() => handleTeamClick(team.team_id)}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-2">
                                  <span className={`font-mono font-bold ${team.is_champion ? 'text-accent-primary' : 'text-text-primary'}`}>
                                    #{team.rank}
                                  </span>
                                  {!team.is_champion && madePlayoffs && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-accent-primary/60" />
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-text-primary font-medium">{team.team_name}</span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="font-mono text-text-secondary">#{team.regular_season?.rank || 'N/A'}</span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className="font-mono text-text-primary">
                                  {team.regular_season?.wins || 0}-{team.regular_season?.losses || 0}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <span className="font-mono text-text-primary">
                                  {Number(team.regular_season?.points_for || 0).toFixed(1)}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <span className="font-mono text-text-primary">
                                  {Number(team.regular_season?.points_against || 0).toFixed(1)}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <span className={`font-mono font-medium ${
                                  (team.regular_season?.point_differential || 0) >= 0 ? 'text-performance-elite' : 'text-performance-bust'
                                }`}>
                                  {(team.regular_season?.point_differential || 0) >= 0 ? '+' : ''}
                                  {Number(team.regular_season?.point_differential || 0).toFixed(1)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile list — Rank · Team · W-L · PF, tap to expand PA/differential */}
                  <div className="md:hidden">
                    <div className="flex items-center gap-3 pb-2 mb-1 border-b border-fieldline text-xs uppercase tracking-wide text-text-secondary">
                      <span className="w-8">Rank</span>
                      <span className="flex-1">Team</span>
                      <span className="w-12 text-right">W-L</span>
                      <span className="w-16 text-right">PF</span>
                    </div>
                    {getSortedStandings(standings.standings).map((team) => {
                      const madePlayoffs = team.rank <= 6;
                      const isOpen = expandedTeam === team.team_id;
                      return (
                        <div
                          key={team.team_id}
                          className="border-b border-fieldline"
                          style={{
                            boxShadow: team.is_champion
                              ? 'inset 3px 0 0 0 #2FBF71'
                              : madePlayoffs
                                ? 'inset 3px 0 0 0 rgba(47,191,113,0.35)'
                                : 'inset 3px 0 0 0 transparent',
                          }}
                        >
                          <button
                            className="w-full flex items-center gap-3 py-3 text-left"
                            onClick={() => setExpandedTeam(isOpen ? null : team.team_id)}
                          >
                            <span className={`w-8 font-mono font-bold ${team.is_champion ? 'text-accent-primary' : 'text-text-primary'}`}>
                              #{team.rank}
                            </span>
                            <span className="flex-1 min-w-0 truncate text-text-primary font-medium">{team.team_name}</span>
                            <span className="w-12 text-right font-mono text-text-primary">
                              {team.regular_season?.wins || 0}-{team.regular_season?.losses || 0}
                            </span>
                            <span className="w-16 text-right font-mono text-text-primary">
                              {Number(team.regular_season?.points_for || 0).toFixed(1)}
                            </span>
                            {isOpen
                              ? <ChevronUp className="w-4 h-4 text-text-secondary flex-shrink-0" />
                              : <ChevronDown className="w-4 h-4 text-text-secondary flex-shrink-0" />}
                          </button>
                          {isOpen && (
                            <div className="pb-3 pl-11 pr-1 grid grid-cols-2 gap-y-2 text-sm">
                              <span className="text-text-secondary">Points Against</span>
                              <span className="text-right font-mono text-text-primary">
                                {Number(team.regular_season?.points_against || 0).toFixed(1)}
                              </span>
                              <span className="text-text-secondary">Differential</span>
                              <span className={`text-right font-mono font-medium ${
                                (team.regular_season?.point_differential || 0) >= 0 ? 'text-performance-elite' : 'text-performance-bust'
                              }`}>
                                {(team.regular_season?.point_differential || 0) >= 0 ? '+' : ''}
                                {Number(team.regular_season?.point_differential || 0).toFixed(1)}
                              </span>
                              <span className="text-text-secondary">Reg. Season Rank</span>
                              <span className="text-right font-mono text-text-primary">#{team.regular_season?.rank || 'N/A'}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaguePage;
